/**
 * Per-tooth shape editing for a placed wireframe filter.
 *
 * Teeth are recovered from the filter image itself: every closed white outline
 * encloses its own black interior. Each tooth keeps its traced outline as the
 * base shape, and a few handles deform it. Moving a handle bends only the arc
 * between its neighbors, so the traced detail elsewhere stays intact.
 */

import type { Point2D } from "./types";

export const WIRE_TOOTH_DEFAULT_POINTS = 6;
export const WIRE_TOOTH_MAX_POINTS = 12;

export interface WireToothHandle {
  /** Arc length along the base outline where the handle sits */
  s: number;
  dx: number;
  dy: number;
}

export interface WireTooth {
  id: string;
  /** Traced outline, dense and closed, in filter pixels */
  base: Point2D[];
  /** Cumulative arc length at each base point */
  cum: number[];
  perimeter: number;
  /** Sorted by `s` */
  handles: WireToothHandle[];
  /**
   * Whole-tooth size / position, applied after the handle shape. Scale is about
   * the tooth's own center. All optional; absent means unchanged.
   */
  scaleX?: number;
  scaleY?: number;
  shiftX?: number;
  shiftY?: number;
}

function centerOf(t: WireTooth): Point2D {
  let x = 0;
  let y = 0;
  for (const p of t.base) {
    x += p.x;
    y += p.y;
  }
  return { x: x / t.base.length, y: y / t.base.length };
}

function hasTransform(t: WireTooth): boolean {
  return (
    (t.scaleX ?? 1) !== 1 ||
    (t.scaleY ?? 1) !== 1 ||
    (t.shiftX ?? 0) !== 0 ||
    (t.shiftY ?? 0) !== 0
  );
}

/** Shape space → displayed space. */
function toDisplay(t: WireTooth, c: Point2D, p: Point2D): Point2D {
  return {
    x: c.x + (p.x - c.x) * (t.scaleX ?? 1) + (t.shiftX ?? 0),
    y: c.y + (p.y - c.y) * (t.scaleY ?? 1) + (t.shiftY ?? 0),
  };
}

/** Displayed space → shape space. */
function fromDisplay(t: WireTooth, c: Point2D, p: Point2D): Point2D {
  return {
    x: c.x + (p.x - c.x - (t.shiftX ?? 0)) / Math.max(0.01, t.scaleX ?? 1),
    y: c.y + (p.y - c.y - (t.shiftY ?? 0)) / Math.max(0.01, t.scaleY ?? 1),
  };
}

export const TOOTH_SCALE_MIN = 0.4;
export const TOOTH_SCALE_MAX = 2.4;

function clampScale(n: number): number {
  return Math.min(TOOTH_SCALE_MAX, Math.max(TOOTH_SCALE_MIN, n));
}

/** Change a tooth's overall width / height / position. */
export function setWireToothTransform(
  t: WireTooth,
  patch: Partial<Pick<WireTooth, "scaleX" | "scaleY" | "shiftX" | "shiftY">>
): WireTooth {
  const next = { ...t, ...patch };
  if (patch.scaleX != null) next.scaleX = clampScale(patch.scaleX);
  if (patch.scaleY != null) next.scaleY = clampScale(patch.scaleY);
  return next;
}

export interface ToothBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
}

function boundsOf(pts: Point2D[]): ToothBox {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { x0, y0, x1, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
}

function shapedUntransformed(t: WireTooth): Point2D[] {
  if (t.handles.every((h) => h.dx === 0 && h.dy === 0)) return t.base;
  return t.base.map((p, i) => {
    const o = offsetAt(t, t.cum[i]);
    return { x: p.x + o.dx, y: p.y + o.dy };
  });
}

/** Axis-aligned box of the tooth before size/position transform. */
export function wireToothLocalBounds(t: WireTooth): ToothBox {
  return boundsOf(shapedUntransformed(t));
}

/** Axis-aligned box of the tooth as shown on the photo. */
export function wireToothDisplayBounds(t: WireTooth): ToothBox {
  return boundsOf(wireToothContour(t));
}

export type ToothSizeHandle = "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";

/**
 * Resize a tooth about its center from a dragged box handle.
 * `lockAspect` keeps width and height in the same ratio.
 */
export function resizeWireToothByHandle(
  t: WireTooth,
  handle: ToothSizeHandle,
  target: Point2D,
  lockAspect = false
): WireTooth {
  const c = wireToothCenter(t);
  const local = wireToothLocalBounds(t);
  const halfW = Math.max(2, local.w / 2);
  const halfH = Math.max(2, local.h / 2);
  const rx = Math.abs(target.x - c.x) / halfW;
  const ry = Math.abs(target.y - c.y) / halfH;
  const affectX = handle.includes("e") || handle.includes("w");
  const affectY = handle.includes("n") || handle.includes("s");
  let scaleX = t.scaleX ?? 1;
  let scaleY = t.scaleY ?? 1;
  if (lockAspect && affectX && affectY) {
    const s = clampScale((rx + ry) / 2);
    scaleX = s;
    scaleY = s;
  } else {
    if (affectX) scaleX = clampScale(rx);
    if (affectY) scaleY = clampScale(ry);
    if (lockAspect) {
      const s = affectX ? scaleX : scaleY;
      scaleX = s;
      scaleY = s;
    }
  }
  return setWireToothTransform(t, { scaleX, scaleY });
}

/** Slide the whole tooth in photo pixels. */
export function moveWireTooth(
  t: WireTooth,
  dx: number,
  dy: number,
  bounds: { width: number; height: number }
): WireTooth {
  const c = wireToothCenter(t);
  const shiftX = (t.shiftX ?? 0) + dx;
  const shiftY = (t.shiftY ?? 0) + dy;
  const nx = Math.max(8, Math.min(bounds.width - 8, c.x + dx));
  const ny = Math.max(8, Math.min(bounds.height - 8, c.y + dy));
  return setWireToothTransform(t, {
    shiftX: shiftX + (nx - (c.x + dx)),
    shiftY: shiftY + (ny - (c.y + dy)),
  });
}

/** True when the tooth differs from its traced shape. */
export function isWireToothEdited(t: WireTooth): boolean {
  return hasTransform(t) || t.handles.some((h) => h.dx !== 0 || h.dy !== 0);
}

/** Center of the tooth as displayed. */
export function wireToothCenter(t: WireTooth): Point2D {
  return toDisplay(t, centerOf(t), centerOf(t));
}

export interface WireTeethModel {
  teeth: WireTooth[];
  /** Filter pixel size */
  width: number;
  height: number;
  strokeWidth: number;
}

function buildArc(base: Point2D[]): { cum: number[]; perimeter: number } {
  const cum = [0];
  for (let i = 1; i < base.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(base[i].x - base[i - 1].x, base[i].y - base[i - 1].y));
  }
  const last = base[base.length - 1];
  const perimeter = cum[cum.length - 1] + Math.hypot(base[0].x - last.x, base[0].y - last.y);
  return { cum, perimeter };
}

function wrapS(s: number, perimeter: number): number {
  return ((s % perimeter) + perimeter) % perimeter;
}

function defaultHandles(base: Point2D[], cum: number[], perimeter: number): WireToothHandle[] {
  // Start at the gum-line zenith so handles land on familiar spots.
  let top = 0;
  for (let i = 1; i < base.length; i++) if (base[i].y < base[top].y) top = i;
  const s0 = cum[top];
  return Array.from({ length: WIRE_TOOTH_DEFAULT_POINTS }, (_, k) => ({
    s: wrapS(s0 + (k * perimeter) / WIRE_TOOTH_DEFAULT_POINTS, perimeter),
    dx: 0,
    dy: 0,
  })).sort((a, b) => a.s - b.s);
}

function basePointAt(t: WireTooth, s: number): Point2D {
  const target = wrapS(s, t.perimeter);
  let lo = 0;
  let hi = t.cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t.cum[mid] <= target) lo = mid;
    else hi = mid - 1;
  }
  const a = t.base[lo];
  const b = t.base[(lo + 1) % t.base.length];
  const segEnd = lo + 1 < t.cum.length ? t.cum[lo + 1] : t.perimeter;
  const len = segEnd - t.cum[lo];
  const f = len > 0 ? (target - t.cum[lo]) / len : 0;
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
}

/** Catmull-Rom interpolation of handle offsets around the closed outline. */
function offsetAt(t: WireTooth, s: number): { dx: number; dy: number } {
  const h = t.handles;
  const n = h.length;
  if (n === 0) return { dx: 0, dy: 0 };
  const target = wrapS(s, t.perimeter);
  let i = n - 1;
  for (let k = 0; k < n; k++) {
    if (h[k].s <= target) i = k;
  }
  const p1 = h[i];
  const p2 = h[(i + 1) % n];
  const p0 = h[(i - 1 + n) % n];
  const p3 = h[(i + 2) % n];
  const span = wrapS(p2.s - p1.s, t.perimeter) || t.perimeter;
  const u = wrapS(target - p1.s, t.perimeter) / span;
  const u2 = u * u;
  const u3 = u2 * u;
  const cr = (a: number, b: number, c: number, d: number) =>
    0.5 * (2 * b + (-a + c) * u + (2 * a - 5 * b + 4 * c - d) * u2 + (-a + 3 * b - 3 * c + d) * u3);
  return {
    dx: cr(p0.dx, p1.dx, p2.dx, p3.dx),
    dy: cr(p0.dy, p1.dy, p2.dy, p3.dy),
  };
}

/** Current (deformed) outline of a tooth, dense, in filter pixels. */
export function wireToothContour(t: WireTooth): Point2D[] {
  const flat = t.handles.every((h) => h.dx === 0 && h.dy === 0);
  const shaped = flat
    ? t.base
    : t.base.map((p, i) => {
        const o = offsetAt(t, t.cum[i]);
        return { x: p.x + o.dx, y: p.y + o.dy };
      });
  if (!hasTransform(t)) return shaped;
  const c = centerOf(t);
  return shaped.map((p) => toDisplay(t, c, p));
}

/** Handle positions on the current outline, in filter pixels. */
export function wireToothHandlePoints(t: WireTooth): Point2D[] {
  const c = hasTransform(t) ? centerOf(t) : null;
  return t.handles.map((h) => {
    const b = basePointAt(t, h.s);
    const p = { x: b.x + h.dx, y: b.y + h.dy };
    return c ? toDisplay(t, c, p) : p;
  });
}

export function moveWireToothHandle(
  t: WireTooth,
  index: number,
  target: Point2D,
  bounds: { width: number; height: number }
): WireTooth {
  const h = t.handles[index];
  if (!h) return t;
  const b = basePointAt(t, h.s);
  const clamped = {
    x: Math.max(0, Math.min(bounds.width, target.x)),
    y: Math.max(0, Math.min(bounds.height, target.y)),
  };
  const { x, y } = hasTransform(t) ? fromDisplay(t, centerOf(t), clamped) : clamped;
  const handles = t.handles.slice();
  handles[index] = { ...h, dx: x - b.x, dy: y - b.y };
  return { ...t, handles };
}

/**
 * Add a handle on the current outline nearest `near`, or in the middle of the
 * widest gap between handles. The outline does not move when a handle is added.
 */
export function addWireToothHandle(t: WireTooth, near?: Point2D): WireTooth {
  if (t.handles.length >= WIRE_TOOTH_MAX_POINTS) return t;
  let s: number;
  if (near) {
    const contour = wireToothContour(t);
    let best = 0;
    let bestD = Infinity;
    contour.forEach((p, i) => {
      const d = (p.x - near.x) ** 2 + (p.y - near.y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    s = t.cum[best];
  } else {
    let gapStart = t.handles[0].s;
    let gap = -1;
    t.handles.forEach((h, i) => {
      const next = t.handles[(i + 1) % t.handles.length];
      const g = wrapS(next.s - h.s, t.perimeter) || t.perimeter;
      if (g > gap) {
        gap = g;
        gapStart = h.s;
      }
    });
    s = wrapS(gapStart + gap / 2, t.perimeter);
  }
  const minGap = t.perimeter / (WIRE_TOOTH_MAX_POINTS * 3);
  const tooClose = t.handles.some((h) => {
    const d = wrapS(h.s - s, t.perimeter);
    return Math.min(d, t.perimeter - d) < minGap;
  });
  if (tooClose) return t;
  const o = offsetAt(t, s);
  const handles = [...t.handles, { s, dx: o.dx, dy: o.dy }].sort((a, b) => a.s - b.s);
  return { ...t, handles };
}

export function removeWireToothHandle(t: WireTooth, index: number): WireTooth {
  if (t.handles.length <= WIRE_TOOTH_DEFAULT_POINTS) return t;
  if (index < 0 || index >= t.handles.length) return t;
  return { ...t, handles: t.handles.filter((_, i) => i !== index) };
}

export function resetWireTooth(t: WireTooth): WireTooth {
  return {
    ...t,
    handles: defaultHandles(t.base, t.cum, t.perimeter),
    scaleX: 1,
    scaleY: 1,
    shiftX: 0,
    shiftY: 0,
  };
}

/**
 * Build teeth straight from traced outlines (no filter image involved).
 * Each outline is resampled to an even point count so handles bend it smoothly.
 */
export function buildWireTeethFromContours(
  contours: Point2D[][],
  width: number,
  height: number,
  strokeWidth = 2
): WireTeethModel {
  const teeth: WireTooth[] = [];
  for (const contour of contours) {
    if (contour.length < 6) continue;
    const { cum: c0, perimeter: p0 } = buildArc(contour);
    if (p0 <= 0) continue;
    const seed: WireTooth = { id: "", base: contour, cum: c0, perimeter: p0, handles: [] };
    const count = 160;
    const base = Array.from({ length: count }, (_, k) => basePointAt(seed, (k * p0) / count));
    const { cum, perimeter } = buildArc(base);
    teeth.push({ id: "", base, cum, perimeter, handles: defaultHandles(base, cum, perimeter) });
  }
  const centerX = (t: WireTooth) => t.base.reduce((s, p) => s + p.x, 0) / t.base.length;
  teeth.sort((a, b) => centerX(a) - centerX(b));
  teeth.forEach((t, i) => (t.id = `wire-tooth-${i + 1}`));
  return { teeth, width, height, strokeWidth };
}

// ---------------------------------------------------------------------------
// Recover teeth from the filter image
// ---------------------------------------------------------------------------

const DIRS = [
  [-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1],
] as const;

function traceBoundary(bin: Uint8Array, w: number, h: number): Point2D[] {
  const fg = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && bin[y * w + x] === 1;
  const start = bin.indexOf(1);
  if (start < 0) return [];
  const sx = start % w;
  const sy = (start - sx) / w;
  const contour: Point2D[] = [{ x: sx, y: sy }];
  let cx = sx;
  let cy = sy;
  let back = 0;
  let second: Point2D | null = null;
  for (let step = 0; step < w * h * 4; step++) {
    let moved = false;
    for (let k = 1; k <= 8; k++) {
      const d = (back + k) % 8;
      const nx = cx + DIRS[d][0];
      const ny = cy + DIRS[d][1];
      if (!fg(nx, ny)) continue;
      const pd = (d + 7) % 8;
      const bx = cx + DIRS[pd][0] - nx;
      const by = cy + DIRS[pd][1] - ny;
      const wasStart = cx === sx && cy === sy;
      cx = nx;
      cy = ny;
      back = Math.max(0, DIRS.findIndex(([dx, dy]) => dx === bx && dy === by));
      moved = true;
      if (!second) second = { x: cx, y: cy };
      else if (wasStart && cx === second.x && cy === second.y) {
        contour.pop();
        return contour;
      }
      contour.push({ x: cx, y: cy });
      break;
    }
    if (!moved) break;
  }
  return contour;
}

function smoothClosed(points: Point2D[], radius: number): Point2D[] {
  const n = points.length;
  if (n < radius * 4) return points;
  return points.map((_, i) => {
    let sx = 0;
    let sy = 0;
    for (let k = -radius; k <= radius; k++) {
      const p = points[(i + k + n) % n];
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / (2 * radius + 1), y: sy / (2 * radius + 1) };
  });
}

function resampleBySpacing(points: Point2D[], spacing: number): Point2D[] {
  const { cum, perimeter } = buildArc(points);
  const count = Math.max(24, Math.min(480, Math.round(perimeter / spacing)));
  const t: WireTooth = { id: "", base: points, cum, perimeter, handles: [] };
  return Array.from({ length: count }, (_, k) => basePointAt(t, (k * perimeter) / count));
}

function dilate(bin: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(bin.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (bin[y * w + x]) {
        out[y * w + x] = 1;
        continue;
      }
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < w && ny < h && bin[ny * w + nx]) out[y * w + x] = 1;
        }
      }
    }
  }
  return out;
}

/**
 * Find every closed tooth outline in a white-on-transparent filter canvas.
 * Returns teeth sorted image-left → image-right, with default handles.
 */
export function extractWireTeeth(canvas: HTMLCanvasElement): WireTeethModel {
  const width = canvas.width;
  const height = canvas.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Could not read the wireframe");
  const { data } = ctx.getImageData(0, 0, width, height);
  const count = width * height;

  const rawStroke = new Uint8Array(count);
  for (let i = 0; i < count; i++) if (data[i * 4 + 3] > 60) rawStroke[i] = 1;
  const stroke = dilate(rawStroke, width, height);

  // Background reachable from the border is outside every tooth.
  const outside = new Uint8Array(count);
  const stack: number[] = [];
  for (let x = 0; x < width; x++) stack.push(x, (height - 1) * width + x);
  for (let y = 0; y < height; y++) stack.push(y * width, y * width + width - 1);
  while (stack.length) {
    const i = stack.pop() as number;
    if (outside[i] || stroke[i]) continue;
    outside[i] = 1;
    const x = i % width;
    if (x > 0) stack.push(i - 1);
    if (x < width - 1) stack.push(i + 1);
    if (i >= width) stack.push(i - width);
    if (i < count - width) stack.push(i + width);
  }

  // Each enclosed hole is one tooth interior.
  const label = new Int32Array(count);
  const regions: { pixels: number[]; x0: number; y0: number; x1: number; y1: number }[] = [];
  for (let i = 0; i < count; i++) {
    if (stroke[i] || outside[i] || label[i]) continue;
    const id = regions.length + 1;
    const region = { pixels: [] as number[], x0: width, y0: height, x1: 0, y1: 0 };
    label[i] = id;
    stack.push(i);
    while (stack.length) {
      const cur = stack.pop() as number;
      region.pixels.push(cur);
      const x = cur % width;
      const y = (cur - x) / width;
      region.x0 = Math.min(region.x0, x);
      region.x1 = Math.max(region.x1, x);
      region.y0 = Math.min(region.y0, y);
      region.y1 = Math.max(region.y1, y);
      const nbs = [x > 0 ? cur - 1 : -1, x < width - 1 ? cur + 1 : -1, cur - width, cur + width];
      for (const nb of nbs) {
        if (nb < 0 || nb >= count || stroke[nb] || outside[nb] || label[nb]) continue;
        label[nb] = id;
        stack.push(nb);
      }
    }
    regions.push(region);
  }

  const biggest = Math.max(0, ...regions.map((r) => r.pixels.length));
  const kept = regions.filter((r) => r.pixels.length >= 20 && r.pixels.length >= biggest * 0.02);

  // Stroke thickness: median horizontal run across the (mostly vertical) contact lines.
  const runs: number[] = [];
  for (let y = 0; y < height; y += 2) {
    let run = 0;
    for (let x = 0; x <= width; x++) {
      const on = x < width && rawStroke[y * width + x] === 1;
      if (on) run++;
      else if (run) {
        if (run <= 12) runs.push(run);
        run = 0;
      }
    }
  }
  runs.sort((a, b) => a - b);
  const strokeWidth = Math.max(1.5, Math.min(8, runs.length ? runs[Math.floor(runs.length / 2)] : 2.5));
  // Interior edge → stroke center: half the stroke plus the 1 px dilation.
  const grow = Math.max(1, Math.round(strokeWidth / 2 + 1));

  const teeth: WireTooth[] = [];
  for (const r of kept) {
    const pad = grow + 2;
    const bx = r.x0 - pad;
    const by = r.y0 - pad;
    const w = r.x1 - r.x0 + 1 + pad * 2;
    const h = r.y1 - r.y0 + 1 + pad * 2;
    let bin: Uint8Array = new Uint8Array(w * h);
    for (const p of r.pixels) {
      const x = (p % width) - bx;
      const y = Math.floor(p / width) - by;
      bin[y * w + x] = 1;
    }
    for (let k = 0; k < grow; k++) bin = dilate(bin, w, h);
    const boundary = traceBoundary(bin, w, h);
    if (boundary.length < 8) continue;
    const local = smoothClosed(boundary, 2).map((p) => ({ x: p.x + bx + 0.5, y: p.y + by + 0.5 }));
    const base = resampleBySpacing(local, 2);
    const { cum, perimeter } = buildArc(base);
    teeth.push({ id: "", base, cum, perimeter, handles: defaultHandles(base, cum, perimeter) });
  }

  const centerX = (t: WireTooth) => t.base.reduce((s, p) => s + p.x, 0) / t.base.length;
  teeth.sort((a, b) => centerX(a) - centerX(b));
  teeth.forEach((t, i) => (t.id = `wire-tooth-${i + 1}`));
  return { teeth, width, height, strokeWidth };
}

/** Redraw the filter (white strokes on transparent) from the current tooth shapes. */
export function renderWireTeethFilter(model: WireTeethModel): string {
  const canvas = document.createElement("canvas");
  canvas.width = model.width;
  canvas.height = model.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not draw the edited wireframe");
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = model.strokeWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (const tooth of model.teeth) {
    const pts = wireToothContour(tooth);
    if (pts.length < 3) continue;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
    ctx.stroke();
  }
  return canvas.toDataURL("image/png");
}
